import Icon from '../Icon.jsx';
import { Avatar, BROOK, BedMapsIcon, ButtonLink, Check, ClosingBand, HART, PageLabel, QuoteCard, RequestBar, ServiceRow } from '../parts.jsx';
import { KITCHEN_TAP_PHOTO, appLinks, unsplash } from '../navigation.js';
import { homeJobPath } from '../../marketplace.js';

const serviceGroups = [
  { id: 'repairs-and-maintenance', title: 'Repairs & Maintenance', services: [['hammer', 'Handyman'], ['droplets', 'Plumbing'], ['zap', 'Electrical'], ['flame', 'Heating / Gas'], ['key-round', 'Locksmiths'], ['washing-machine', 'Appliance Repair']] },
  { id: 'property-services', title: 'Property Services', services: [['sparkles', 'Cleaning'], ['bug', 'Pest Control'], ['sofa', 'Furniture / Assembly'], ['truck', 'Moving'], ['trash-2', 'Waste / Clearance'], ['trees', 'Grounds / Exterior']] },
  { id: 'compliance-services', title: 'Compliance Services', tone: 'sage', services: [['shield-check', 'Gas Safety'], ['plug-zap', 'Electrical Inspections'], ['fire-extinguisher', 'Fire Safety'], ['file-badge', 'Energy / Property Certificates'], ['calendar-check', 'Other Scheduled Checks']] },
];

const repairStatus = [
  { icon: 'check', done: true, title: 'Quote accepted', line: 'Brook Plumbing · £85' },
  { icon: 'check', done: true, title: 'Visit booked', line: 'Tomorrow at 10' },
  { icon: 'hourglass', done: false, title: 'Repair fixed', line: 'After the visit' },
];

const landlordChoices = [
  { label: 'Yes, send them my request', selected: true },
  { label: 'Not this time' },
  { label: 'Never contact my landlord' },
];

function Step({ number, title, text, children }) {
  return <li className="mk-step">
    <div className="mk-step-visual">{children}</div>
    <div className="mk-step-text">
      <h3 className="mk-step-head"><span className="mk-step-number is-36">{number}</span>{title}</h3>
      <p className="mk-body">{text}</p>
    </div>
  </li>;
}

export default function HomeServicesPage() {
  return <>
    <section className="mk-container mk-hero" aria-labelledby="home-services-title">
      <div className="mk-hero-copy">
        <PageLabel icon="wrench">Home &amp; Services</PageLabel>
        <h1 className="mk-display" id="home-services-title">Book a repair.</h1>
        <p className="mk-lead">Take a photo of the problem and we will find local, checked tradespeople to quote. You choose who comes, and when.</p>
        <RequestBar/>
        <div className="mk-checks"><Check>A photo and up to three questions</Check><Check>Nothing is booked until you choose</Check></div>
      </div>
      <div className="mk-hero-visual is-quotes">
        <div className="mk-job-card">
          <img className="mk-job-photo" src={unsplash(KITCHEN_TAP_PHOTO, 240)} alt={KITCHEN_TAP_PHOTO.alt} width="72" height="72"/>
          <div className="mk-job-text"><p className="mk-job-title">Your dripping kitchen tap</p><p className="mk-job-status">3 quotes so far. Take your time.</p></div>
        </div>
        <QuoteCard {...BROOK}/>
        <QuoteCard {...HART}/>
      </div>
    </section>

    <section className="mk-container mk-section is-after-hero" id="find-local-trades" aria-labelledby="trades-title">
      <h2 className="mk-h2" id="trades-title">Find Local Trades</h2>
      <div className="mk-service-groups">
        {serviceGroups.map((group) => <div className={`mk-service-group ${group.tone === 'sage' ? 'is-sage' : ''}`} id={group.id} key={group.id}>
          <h3 className="mk-service-group-title">{group.title}</h3>
          <ul>{group.services.map(([icon, name]) => <li key={name}><ServiceRow icon={icon} name={name} to={homeJobPath(name)} tone={group.tone === 'sage' ? 'sage' : 'heather'}/></li>)}</ul>
        </div>)}
      </div>
    </section>

    <section className="mk-band is-white" id="how-it-works" aria-labelledby="steps-title">
      <div className="mk-container mk-stack is-40">
        <h2 className="mk-h2" id="steps-title">How It Works</h2>
        <ol className="mk-steps">
          <Step number="1" title="Take a Photo" text="Show us the problem. Answer up to three quick questions.">
            <div className="mk-question">
              <p className="mk-question-text">What is the tap doing?</p>
              <span className="mk-option is-selected"><Icon name="check" size={18}/>Dripping</span>
              <span className="mk-option">Leaking underneath</span>
              <span className="mk-option">Will not turn off</span>
              <p className="mk-question-count">Question 2 of 3</p>
            </div>
          </Step>
          <Step number="2" title="Compare Quotes" text="Checked local tradespeople send a price and a time. Nothing is booked until you choose.">
            <QuoteCard {...BROOK}/>
            <QuoteCard {...HART}/>
          </Step>
          <Step number="3" title="Accept a Quote" text="Pick the one that suits you. Message them. Track the repair until it is fixed.">
            <div className="mk-booked">
              <span className="mk-booked-tick"><Icon name="check" size={26}/></span>
              <p className="mk-booked-title">Booked</p>
              <p className="mk-booked-line">Brook Plumbing, tomorrow at 10</p>
              <Check>Verified company</Check>
            </div>
          </Step>
        </ol>
      </div>
    </section>

    <section className="mk-container mk-section" id="track-your-repairs" aria-labelledby="track-title">
      <div className="mk-split is-centred">
        <div className="mk-split-copy is-520 is-gap-24">
          <h2 className="mk-h2" id="track-title">Track Your Repairs</h2>
          <p className="mk-body">Message the tradesperson and check the status of your repair.</p>
          <ul className="mk-status-list">{repairStatus.map((status) => <li key={status.title} className={status.done ? 'is-done' : ''}>
            <Icon name={status.icon} size={22}/>
            <span className="mk-status-text"><span className="mk-status-title">{status.title}</span><span className="mk-status-line">{status.line}</span></span>
          </li>)}</ul>
        </div>
        <div className="mk-chat">
          <div className="mk-chat-head">
            <Avatar initials="BP" size={44}/>
            <div className="mk-chat-who"><p className="mk-chat-name">Brook Plumbing</p><Check>Verified company</Check></div>
          </div>
          <p className="mk-bubble is-received">I will bring a new washer and cartridge, just in case.</p>
          <p className="mk-bubble is-sent">Thank you. There is parking outside.</p>
          <p className="mk-bubble is-received">Great, see you at 10.</p>
          <div className="mk-chat-input"><span>Write a message</span><span className="mk-chat-send"><Icon name="send" size={18}/></span></div>
        </div>
      </div>
    </section>

    <section className="mk-band is-wash" id="send-repairs-to-your-landlord" aria-labelledby="landlord-title">
      <div className="mk-container mk-split is-centred">
        <div className="mk-split-copy is-520">
          <PageLabel icon="house">Report a Repair</PageLabel>
          <h2 className="mk-h2" id="landlord-title">Send Repairs to Your Landlord</h2>
          <p className="mk-body">Add your landlord&apos;s repair contact and we send them your request. Or choose never to contact them. It is up to you.</p>
          <p className="mk-bedmaps-note"><BedMapsIcon size={28}/>Is your housing provider on BedMaps? Your repair goes straight to them.</p>
        </div>
        <div className="mk-choice-card">
          <p className="mk-choice-question">Should we tell your landlord?</p>
          {landlordChoices.map((choice) => <span key={choice.label} className={`mk-choice ${choice.selected ? 'is-selected' : ''}`}><Icon name={choice.selected ? 'circle-check' : 'circle'} size={20}/>{choice.label}</span>)}
          <div className="mk-choice-field">
            <p className="mk-choice-field-label">Landlord&apos;s repair contact</p>
            <p className="mk-choice-field-value">Brook Housing, repairs team</p>
          </div>
        </div>
      </div>
    </section>

    <ClosingBand title="Book a Repair" text="Take a photo. Get quotes from checked local tradespeople." wideCopy>
      <ButtonLink to={appLinks.getQuotes} variant="white" size={56}>Get quotes</ButtonLink>
    </ClosingBand>
  </>;
}
